// bfcache 復帰時にフォームが永久に無効化されたままになるのを防ぐため、ページ単位で 1 回だけ
// pageshow listener を登録する。CommentForm は create.js.erb 内で投稿成功のたびに new される
// ため、constructor 内で window に listener を付けるとリスナーが累積して旧インスタンスを GC
// から守ってしまう。モジュールレベルで一度だけ DOM 走査して状態をリセットする
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('.js-comment-form form').forEach((form) => {
    const textarea = form.querySelector<HTMLTextAreaElement>('textarea');
    const submitButton = form.querySelector<SubmitElement>('[type=submit]');
    if (textarea) textarea.readOnly = false;
    if (submitButton?.dataset.disabledByCommentForm === 'true') {
      submitButton.disabled = false;
      delete submitButton.dataset.disabledByCommentForm;
    }
  });
});

type SubmitElement = HTMLButtonElement | HTMLInputElement;

export default class CommentForm {
  private initiators: NodeListOf<Element>;

  constructor(selector: string) {
    this.initiators = document.querySelectorAll(selector);

    this.getFormElements().forEach((form) => {
      if (!form) return;
      const submitButton = this.getSubmitButton(form);
      const fileInput = this.getFileInput(form);
      const textarea = this.getTextarea(form);

      if (!submitButton || !fileInput || !textarea) return;

      // create.js.erb は投稿成功のたびに `new Coorum.CommentForm('.js-comment-form')` を呼ぶため、
      // 同じ form に submit / click ハンドラが累積していく。submit ハンドラで
      // `stopImmediatePropagation` を呼ぶ実装にしたため、2 回目以降のインスタンスの handler が
      // 「readOnly=true」を検知して propagation を止めると、1 回目のインスタンスが readOnly を立てた
      // 直後に Rails UJS の handleRemote (document delegate) まで届かず、初回送信そのものが
      // 握り潰される。form 単位で 1 度だけ初期化するようガードする
      if (form.dataset.commentFormInitialized === 'true') return;
      form.dataset.commentFormInitialized = 'true';

      this.bindSubmitGuard({ form, submitButton, fileInput, textarea });
    });
  }

  private bindSubmitGuard({
    form,
    submitButton,
    fileInput,
    textarea,
  }: {
    form: HTMLFormElement;
    submitButton: SubmitElement;
    fileInput: HTMLInputElement;
    textarea: HTMLTextAreaElement;
  }): void {
    // ダブルクリックでの二重送信を同期的にブロックする。submit イベント時に click ハンドラを
    // 追加する方式だと、ファイル添付ありの `<button type="submit">` (f.button) では Rails UJS の
    // `data-disable-with` 自動 disable も効かないため、わずかなタイミングで 2 回目の click が
    // submit に到達するケースがある。textarea.readOnly が submit ハンドラ内で同期的に true に
    // なるので、それを click 時点で確認して以降の click を握り潰す。preventDefault に加えて
    // stopImmediatePropagation も呼び、Rails UJS の handleRemote (document 上に delegate) へ
    // click が伝播するのも止める
    submitButton.addEventListener('click', (event) => {
      if (textarea.readOnly) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    });

    form.addEventListener('submit', (event) => {
      // Enter キー連打/長押し等で setTimeout の disabled 化が間に合わないうちに
      // 次の submit が走るケースを防ぐ。textarea.readOnly が既に true なら処理中とみなし
      // submit を握り潰す。Rails UJS の handleRemote は document 上の delegate listener で
      // event.defaultPrevented を見ずに AJAX を発火させるため、stopImmediatePropagation で
      // document への伝播自体を止める必要がある
      if (textarea.readOnly) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const attachmentItems = this.setAttachmentItems(form);
      const attachmentDeleteButtons =
        this.setAttachmentDeleteButtons(attachmentItems);

      textarea.readOnly = true;

      // submit ボタン本体に disabled 属性を付与してダブルクリック・bfcache 復帰時の再送信を抑止する。
      // submit イベント中に同期で disabled にすると一部ブラウザが送信をキャンセルするため次 tick で実行する。
      // releaseDisable で本ライブラリが付与した disabled だけを解除できるよう data 属性で印を付ける
      // (権限不足等でサーバ側が初期 disabled にしたケースを誤って有効化しないため)。
      // バリデーションエラー等で AJAX レスポンスが setTimeout より先に到達して releaseDisable が
      // 走った場合 (textarea.readOnly が false に戻っている) は disabled 化をスキップしてボタンが
      // 永久に無効化されないようにする
      setTimeout(() => {
        if (!textarea.readOnly) return;
        submitButton.dataset.disabledByCommentForm = 'true';
        submitButton.disabled = true;
      }, 0);

      [fileInput, ...attachmentDeleteButtons].forEach((element) => {
        element?.addEventListener('click', this.disableClick);
      });
    });
  }

  getFormElements(): (HTMLFormElement | null)[] {
    return Array.from(this.initiators).map((element) =>
      element.querySelector<HTMLFormElement>('form')
    );
  }

  getSubmitButton(form: HTMLFormElement | null): SubmitElement | null {
    return form?.querySelector<SubmitElement>('[type=submit]') ?? null;
  }

  getFileInput(form: HTMLFormElement | null): HTMLInputElement | null {
    return form?.querySelector<HTMLInputElement>('[type=file]') ?? null;
  }

  getTextarea(form: HTMLFormElement | null): HTMLTextAreaElement | null {
    return form?.querySelector<HTMLTextAreaElement>('textarea') ?? null;
  }

  setAttachmentContainers(form: HTMLFormElement): HTMLElement[] {
    return Array.from(
      form.querySelectorAll<HTMLElement>('[data-attachment-item-list]')
    );
  }

  setAttachmentItems(form: HTMLFormElement): HTMLElement[] {
    return Array.from(
      form.querySelectorAll<HTMLElement>('[data-attachment-name]')
    );
  }

  setAttachmentDeleteButtons(
    attachmentItems: HTMLElement[]
  ): (HTMLButtonElement | null)[] {
    return attachmentItems.length
      ? attachmentItems.map((item) =>
          item.querySelector<HTMLButtonElement>('[type=button]')
        )
      : [];
  }

  disableClick(event: Event): void {
    event.preventDefault();
  }

  releaseDisable(): void {
    this.getFormElements().forEach((form) => {
      if (!form) return;
      const textarea = this.getTextarea(form);
      const submitButton = this.getSubmitButton(form);
      if (textarea) textarea.readOnly = false;
      // 本ライブラリが disabled にしたボタンだけを再有効化する
      // (権限不足でサーバ側が disabled にしたボタンを誤って有効化しないため)
      if (submitButton?.dataset.disabledByCommentForm === 'true') {
        submitButton.disabled = false;
        delete submitButton.dataset.disabledByCommentForm;
      }

      this.getFileInput(form)?.removeEventListener('click', this.disableClick);
    });
  }

  resetForm(): this {
    this.getFormElements().forEach((form) => {
      if (!form) return;
      const textarea = this.getTextarea(form);
      const fileInput = this.getFileInput(form);
      const attachmentItems = this.setAttachmentItems(form);
      const attachmentContainers = this.setAttachmentContainers(form);

      if (textarea) {
        textarea.value = '';
        textarea.dispatchEvent(new Event('change'));
      }
      if (fileInput) {
        fileInput.value = '';
        fileInput.dispatchEvent(new Event('change'));
      }

      attachmentItems.forEach((item) => {
        item.remove();
      });

      attachmentContainers.forEach((container) => {
        container.ariaHidden = 'true';
      });
    });

    return this;
  }
}

export { CommentForm };
